import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import Popup from "components/popup/Popup";
import type { ConfirmContextValue, ConfirmOptions } from "./emailBuilderConfirmTypes";
import "./emailBuilderCompactPopupActions.scss";

const ConfirmContext = createContext<ConfirmContextValue | null>(null);

export function useConfirm(): ConfirmContextValue {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error("useConfirm must be used within EmailBuilderConfirmProvider");
  }
  return context;
}

export function EmailBuilderConfirmProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const [confirmRequest, setConfirmRequest] = useState<ConfirmOptions | null>(null);
  const resolveConfirmRef = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback((options: ConfirmOptions) => {
    return new Promise<boolean>(resolve => {
      resolveConfirmRef.current = resolve;
      setConfirmRequest(options);
    });
  }, []);

  const finishConfirm = useCallback((result: boolean) => {
    setConfirmRequest(null);
    const resolve = resolveConfirmRef.current;
    resolveConfirmRef.current = null;
    resolve?.(result);
  }, []);

  const contextValue = useMemo(() => ({ confirm }), [confirm]);

  return (
    <ConfirmContext.Provider value={contextValue}>
      {children}
      {confirmRequest ? (
        <div className="email-builder-compact-popup">
          <Popup
          open
          type={confirmRequest.destructive ? "delete" : "warning"}
          title={confirmRequest.title}
          description={confirmRequest.description}
          closable
          onCancel={() => finishConfirm(false)}
          cancelText={confirmRequest.cancelLabel ?? t("Cancel")}
          okText={confirmRequest.confirmLabel ?? t("Confirm")}
          onOk={() => finishConfirm(true)}
          onOkType={confirmRequest.destructive ? "error" : "primary"}
          backDropCancel
          compactActions
          />
        </div>
      ) : null}
    </ConfirmContext.Provider>
  );
}
